import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus,
  ArrowRight,
  XCircle,
  Clock,
  Trash,
  Spinner,
  X,
  User,
  CalendarBlank,
  Package,
  Warning,
  FileText,
  CheckCircle,
  ArrowSquareOut
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const DEPARTMENTS = [
  'Kitchen',
  'Housekeeping',
  'Front Office',
  'Food & Beverage',
  'Maintenance',
  'Spa',
  'Security',
  'Finance',
  'General Management'
];

const URGENCY_COLORS = {
  low: 'bg-slate-100 text-slate-600',
  medium: 'bg-[#38B2AC]/10 text-[#0F766E]',
  high: 'bg-[#F4A261]/10 text-[#B45309]',
  critical: 'bg-[#E63946]/10 text-[#E63946]'
};

const STATUS_COLORS = {
  pending_conversion: 'bg-[#F4A261]/10 text-[#B45309]',
  converted_to_po: 'bg-[#2D6A4F]/10 text-[#2D6A4F]',
  declined: 'bg-[#E63946]/10 text-[#E63946]',
  returned: 'bg-orange-100 text-orange-700',
  // Legacy status mappings
  pending: 'bg-[#F4A261]/10 text-[#B45309]',
  approved: 'bg-[#2D6A4F]/10 text-[#2D6A4F]',
  rejected: 'bg-[#E63946]/10 text-[#E63946]',
  queried: 'bg-[#0F52BA]/10 text-[#0F52BA]'
};

const STATUS_LABELS = {
  pending_conversion: 'Pending Conversion',
  converted_to_po: 'Converted to PO',
  declined: 'Declined',
  returned: 'Returned to Buyer',
  // Legacy mappings
  pending: 'Pending Conversion',
  approved: 'Converted to PO',
  rejected: 'Declined',
  queried: 'Pending Conversion'
};

const RequisitionsPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [requisitions, setRequisitions] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('filter') || 'all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [selectedReq, setSelectedReq] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [returnReason, setReturnReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  
  // Replenishment requisitions
  const [replenishReqs, setReplenishReqs] = useState([]);
  const [rejectReplenishDialog, setRejectReplenishDialog] = useState({ open: false, req: null });
  const [rejectReplenishReason, setRejectReplenishReason] = useState('');
  
  // Split by vendor
  const [splitDialog, setSplitDialog] = useState({ open: false, req: null });
  const [splitAssignments, setSplitAssignments] = useState([]);
  const [splitSubmitting, setSplitSubmitting] = useState(false);

  // Form state for new PR
  const [formData, setFormData] = useState({
    department_name: '',
    budget_code: '',
    urgency_level: 'medium',
    notes: '',
    items: [{ item_name: '', quantity: 1, unit: 'units', estimated_unit_price: 0, preferred_vendor_id: '' }]
  });

  const isPurchasingManager = user?.role === 'purchasing_manager' || user?.role === 'system_admin';
  const isPendingFilterActive = activeTab === 'pending_conversion';

  // Pilot Mode — when active, dept dropdown is restricted to the pilot dept only
  const [pilotMode, setPilotMode] = useState({ enabled: false, department: null });
  useEffect(() => {
    const pid = user?.property_ids?.[0];
    if (!pid) return;
    const load = async () => {
      try {
        const headers = getAuthHeaders();
        const res = await axios.get(`${API_URL}/api/property-settings/${pid}/pilot-mode`, headers);
        setPilotMode({ enabled: Boolean(res.data.pilot_enabled), department: res.data.pilot_department || null });
      } catch (_) {/* ignore */}
    };
    load();
    const handler = (e) => {
      if (e?.detail) {
        setPilotMode({ enabled: !!e.detail.enabled, department: e.detail.department || null });
      } else {
        load();
      }
    };
    window.addEventListener('pilot-mode-changed', handler);
    return () => window.removeEventListener('pilot-mode-changed', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.property_ids?.[0]]);
  const allowedDepartments = pilotMode.enabled && pilotMode.department ? [pilotMode.department] : DEPARTMENTS;
  // Auto-set the department to the pilot dept when opening the dialog
  useEffect(() => {
    if (pilotMode.enabled && pilotMode.department && formData.department_name !== pilotMode.department) {
      setFormData(prev => ({ ...prev, department_name: pilotMode.department }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pilotMode.enabled, pilotMode.department]);

  // Sync URL params with active tab
  useEffect(() => {
    const filter = searchParams.get('filter');
    if (filter && filter !== activeTab) {
      setActiveTab(filter);
    }
  }, [searchParams]);

  // Update URL when tab changes
  const handleTabChange = (value) => {
    setActiveTab(value);
    if (value === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ filter: value });
    }
  };

  const clearFilter = () => {
    setActiveTab('all');
    setSearchParams({});
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Allow deep-linking from the Open PRs report → /requisitions?selected=<id>
  // Auto-switch to the correct tab and scroll the row into view with a pulse highlight.
  useEffect(() => {
    const selectedId = searchParams.get('selected');
    if (!selectedId || !requisitions.length) return;
    const target = requisitions.find(r => r.id === selectedId);
    if (!target) return;
    // Switch to a tab that contains this PR.
    const statusToTab = {
      pending_conversion: 'pending_conversion',
      approved: 'all',
      returned: 'all',
      converted_to_po: 'converted',
      declined: 'declined',
    };
    const desiredTab = statusToTab[target.status] || 'all';
    if (desiredTab !== activeTab) {
      setActiveTab(desiredTab);
    }
    // Defer scroll until after re-render with the right filter applied.
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-req-anchor="${selectedId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-[#3D1D72]', 'ring-offset-2');
        setTimeout(() => {
          el.classList.remove('ring-2', 'ring-[#3D1D72]', 'ring-offset-2');
        }, 2500);
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requisitions, searchParams]);

  const fetchData = async () => {
    try {
      const [reqRes, vendorRes, replenishRes] = await Promise.all([
        axios.get(`${API_URL}/api/requisitions`, getAuthHeaders()),
        axios.get(`${API_URL}/api/vendors`, getAuthHeaders()),
        axios.get(`${API_URL}/api/replenishment-requisitions`, getAuthHeaders()).catch(() => ({ data: [] }))
      ]);
      setRequisitions(reqRes.data);
      setVendors(vendorRes.data);
      setReplenishReqs(replenishRes.data || []);
    } catch (error) {
      toast.error('Failed to load purchase requisitions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/requisitions`, formData, getAuthHeaders());
      toast.success('Purchase requisition submitted successfully');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit purchase requisition');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (!selectedReq || !declineReason.trim()) {
      toast.error('Please provide a reason for declining');
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(
        `${API_URL}/api/requisitions/${selectedReq.id}/action`,
        { action: 'decline', message: declineReason },
        getAuthHeaders()
      );
      toast.success('Purchase requisition declined');
      setDeclineDialogOpen(false);
      setSelectedReq(null);
      setDeclineReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline purchase requisition');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConvertToPO = (req) => {
    // Navigate to PO page with requisition pre-selected
    navigate(`/purchase-orders?convert=${req.id}`);
  };

  const handleReturn = async () => {
    if (!selectedReq || !returnReason.trim()) {
      toast.error('Please provide a reason for returning');
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(
        `${API_URL}/api/requisitions/${selectedReq.id}/action`,
        { action: 'return', message: returnReason },
        getAuthHeaders()
      );
      toast.success('Requisition returned to buyer');
      setReturnDialogOpen(false);
      setSelectedReq(null);
      setReturnReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to return requisition');
    } finally {
      setSubmitting(false);
    }
  };

  const openDeclineDialog = (req) => {
    setSelectedReq(req);
    setDeclineReason('');
    setDeclineDialogOpen(true);
  };

  const openReturnDialog = (req) => {
    setSelectedReq(req);
    setReturnReason('');
    setReturnDialogOpen(true);
  };

  const handleApproveReplenish = async (req) => {
    try {
      await axios.put(`${API_URL}/api/replenishment-requisitions/${req.id}/action`,
        { action: 'approve' }, getAuthHeaders());
      toast.success(`Replenishment approved for ${req.item_name}. Create a PO now.`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleRejectReplenish = async () => {
    if (!rejectReplenishReason.trim()) return;
    try {
      await axios.put(`${API_URL}/api/replenishment-requisitions/${rejectReplenishDialog.req.id}/action`,
        { action: 'reject', reason: rejectReplenishReason }, getAuthHeaders());
      toast.success('Replenishment requisition rejected');
      setRejectReplenishDialog({ open: false, req: null });
      setRejectReplenishReason('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // Split by Vendor
  const openSplitDialog = (req) => {
    const items = req.items || [];
    setSplitAssignments(items.map((item, idx) => ({
      item_index: idx,
      item_name: item.item_name,
      quantity: item.quantity,
      unit: item.unit_of_measure || item.unit || 'units',
      estimated_unit_price: item.estimated_unit_price || 0,
      vendor_id: '',
      po_type: 'local'
    })));
    setSplitDialog({ open: true, req });
  };

  const updateSplitAssignment = (idx, field, value) => {
    setSplitAssignments(prev => prev.map((a, i) => i === idx ? { ...a, [field]: value } : a));
  };

  const handleSplitGenerate = async () => {
    const unassigned = splitAssignments.filter(a => !a.vendor_id);
    if (unassigned.length > 0) {
      toast.error(`${unassigned.length} item(s) not assigned to a vendor`);
      return;
    }
    const uniqueVendors = new Set(splitAssignments.map(a => a.vendor_id));
    if (uniqueVendors.size < 2) {
      toast.error('Split requires at least 2 different vendors. Use Convert to PO for a single vendor.');
      return;
    }
    setSplitSubmitting(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/requisitions/${splitDialog.req.id}/split-to-pos`,
        { assignments: splitAssignments.map(a => ({ item_index: a.item_index, vendor_id: a.vendor_id, po_type: a.po_type })) },
        getAuthHeaders()
      );
      toast.success(res.data.message);
      setSplitDialog({ open: false, req: null });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to split');
    } finally { setSplitSubmitting(false); }
  };

  const resetForm = () => {
    setFormData({
      department_name: '',
      budget_code: '',
      urgency_level: 'medium',
      notes: '',
      items: [{ item_name: '', quantity: 1, unit: 'units', estimated_unit_price: 0, preferred_vendor_id: '' }]
    });
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { item_name: '', quantity: 1, unit: 'units', estimated_unit_price: 0, preferred_vendor_id: '' }]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  // Map status for filtering
  const mapStatusForFilter = (status) => {
    const mappings = {
      'pending': 'pending_conversion',
      'approved': 'converted_to_po',
      'rejected': 'declined',
      'queried': 'pending_conversion'
    };
    return mappings[status] || status;
  };

  const filteredRequisitions = requisitions.filter(req => {
    if (activeTab === 'all') return true;
    const mappedStatus = mapStatusForFilter(req.status);
    return mappedStatus === activeTab;
  });

  // Count requisitions by status
  const pendingCount = requisitions.filter(r => 
    ['pending', 'pending_conversion', 'queried'].includes(r.status)
  ).length;
  const convertedCount = requisitions.filter(r => 
    ['approved', 'converted_to_po'].includes(r.status)
  ).length;
  const declinedCount = requisitions.filter(r => 
    ['rejected', 'declined'].includes(r.status)
  ).length;
  const returnedCount = requisitions.filter(r => r.status === 'returned').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0F52BA] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Purchase Requisitions
          </h1>
          <p className="text-slate-500 mt-1">
            {isPurchasingManager ? 'Review and convert purchase requisitions to POs' : 'Submit and track your purchase requisitions'}
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0F52BA] hover:bg-[#0A3D8F]" data-testid="new-requisition-btn">
              <Plus size={20} className="mr-2" />
              New Purchase Requisition
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>New Purchase Requisition</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Department {pilotMode.enabled && <span className="text-xs text-[#3D1D72]">(Pilot Mode locked)</span>}</Label>
                  <Select
                    value={formData.department_name}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, department_name: value }))}
                    required
                    disabled={pilotMode.enabled}
                  >
                    <SelectTrigger data-testid="department-select">
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {allowedDepartments.map(dept => (
                        <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Budget Code</Label>
                  <Input
                    value={formData.budget_code}
                    onChange={(e) => setFormData(prev => ({ ...prev, budget_code: e.target.value }))}
                    placeholder="e.g., F&B-001"
                    required
                    data-testid="budget-code-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Urgency Level</Label>
                <Select
                  value={formData.urgency_level}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, urgency_level: value }))}
                >
                  <SelectTrigger data-testid="urgency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Items Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Requested Items</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn">
                    <Plus size={16} className="mr-1" />
                    Add Item
                  </Button>
                </div>
                {formData.items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 p-3 bg-slate-50 rounded-lg" data-testid={`item-row-${index}`}>
                    <div className="col-span-5">
                      <Input
                        value={item.item_name}
                        onChange={(e) => updateItem(index, 'item_name', e.target.value)}
                        placeholder="Item description"
                        required
                        data-testid={`item-name-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                        placeholder="Qty"
                        required
                        data-testid={`item-qty-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        value={item.unit}
                        onChange={(e) => updateItem(index, 'unit', e.target.value)}
                        placeholder="Unit"
                        data-testid={`item-unit-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.estimated_unit_price}
                        onChange={(e) => updateItem(index, 'estimated_unit_price', parseFloat(e.target.value) || 0)}
                        placeholder="Est. Price"
                        data-testid={`item-price-${index}`}
                      />
                    </div>
                    <div className="col-span-1 flex items-center justify-center">
                      {index > 0 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(index)}
                          className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                          data-testid={`remove-item-${index}`}
                        >
                          <Trash size={16} />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any additional notes or special requirements..."
                  data-testid="notes-textarea"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={submitting} className="bg-[#0F52BA] hover:bg-[#0A3D8F]" data-testid="submit-requisition-btn">
                  {submitting && <Spinner size={18} className="animate-spin mr-2" />}
                  Submit Purchase Requisition
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="all" data-testid="tab-all">
            All ({requisitions.length})
          </TabsTrigger>
          <TabsTrigger value="pending_conversion" data-testid="tab-pending">
            Pending Conversion ({pendingCount})
          </TabsTrigger>
          <TabsTrigger value="converted_to_po" data-testid="tab-converted">
            Converted to PO ({convertedCount})
          </TabsTrigger>
          <TabsTrigger value="declined" data-testid="tab-declined">
            Declined ({declinedCount})
          </TabsTrigger>
          <TabsTrigger value="returned" data-testid="tab-returned">
            Returned ({returnedCount})
          </TabsTrigger>
          {isPurchasingManager && (
            <TabsTrigger value="replenishment" data-testid="tab-replenishment">
              Replenishment ({replenishReqs.filter(r => r.status === 'pending_review').length})
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {isPurchasingManager && isPendingFilterActive ? (
            // Grid view for pending conversion (PM only)
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredRequisitions.length === 0 ? (
                <div className="col-span-full text-center py-12 text-slate-500">
                  <FileText size={48} className="mx-auto mb-4 text-slate-300" />
                  <p>No purchase requisitions pending conversion</p>
                </div>
              ) : (
                filteredRequisitions.map(req => (
                  <PendingConversionCard
                    key={req.id}
                    req={req}
                    onConvert={() => handleConvertToPO(req)}
                    onDecline={() => openDeclineDialog(req)}
                    onReturn={() => openReturnDialog(req)}
                    onSplit={() => openSplitDialog(req)}
                    isProcessing={processingId === req.id}
                  />
                ))
              )}
            </div>
          ) : (
            // List view for all other tabs
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              {filteredRequisitions.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <FileText size={48} className="mx-auto mb-4 text-slate-300" />
                  <p>No purchase requisitions found</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filteredRequisitions.map(req => (
                    <RequisitionRow
                      key={req.id}
                      req={req}
                      isPurchasingManager={isPurchasingManager}
                      onConvert={() => handleConvertToPO(req)}
                      onDecline={() => openDeclineDialog(req)}
                      onReturn={() => openReturnDialog(req)}
                      onSplit={() => openSplitDialog(req)}
                      isProcessing={processingId === req.id}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* Replenishment Requisitions Tab */}
        {isPurchasingManager && (
          <TabsContent value="replenishment" className="space-y-4">
            {replenishReqs.length === 0 ? (
              <div className="text-center py-12 text-slate-500 bg-white rounded-xl border">
                <p>No replenishment requisitions</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
                <div className="p-4 border-b bg-[#14B8A6]/5">
                  <h2 className="text-lg font-semibold text-slate-800">Replenishment Requisitions from Storekeeper</h2>
                  <p className="text-sm text-slate-500">Stock replenishment requests — separate from buyer purchase requisitions</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="replenishment-reqs-table">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Item</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500">Current Stock</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500">Par Level</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500">Qty Requested</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500">Urgency</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Submitted By</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500">Status</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {replenishReqs.map(req => (
                        <tr key={req.id} className={req.status === 'pending_review' ? 'bg-amber-50/30' : ''} data-testid={`pm-replenish-row-${req.id}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-[#14B8A6]/10 text-[#14B8A6]">Replenishment</span>
                            </div>
                            <p className="font-medium text-slate-800 mt-1">{req.item_name}</p>
                            <p className="text-xs text-slate-500">{req.sku} | {req.location_name} | {req.category_name}</p>
                          </td>
                          <td className="px-4 py-3 text-center text-red-600 font-medium">{req.current_stock}</td>
                          <td className="px-4 py-3 text-center">{req.par_level}</td>
                          <td className="px-4 py-3 text-center font-bold">{req.quantity_to_replenish} {req.unit_of_measure}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              req.urgency_level === 'critical' ? 'bg-red-100 text-red-700' :
                              req.urgency_level === 'urgent' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                            }`}>{req.urgency_level?.charAt(0).toUpperCase() + req.urgency_level?.slice(1)}</span>
                          </td>
                          <td className="px-4 py-3 text-slate-600 text-sm">
                            {req.submitted_by_name}
                            <br /><span className="text-xs text-slate-400">{req.created_at ? new Date(req.created_at).toLocaleDateString() : ''}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              req.status === 'pending_review' ? 'bg-amber-100 text-amber-700' :
                              req.status === 'converted_to_po' ? 'bg-green-100 text-green-700' :
                              'bg-red-100 text-red-700'
                            }`}>{req.status === 'pending_review' ? 'Pending' : req.status === 'converted_to_po' ? 'Approved' : 'Rejected'}</span>
                          </td>
                          <td className="px-4 py-3">
                            {req.status === 'pending_review' && (
                              <div className="flex gap-1 justify-center">
                                <Button size="sm" className="bg-[#0F52BA] hover:bg-[#0A3D8F] h-7 text-xs"
                                  onClick={() => handleApproveReplenish(req)} data-testid={`approve-replenish-${req.id}`}>
                                  Approve
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-300"
                                  onClick={() => { setRejectReplenishDialog({ open: true, req }); setRejectReplenishReason(''); }}
                                  data-testid={`reject-replenish-${req.id}`}>
                                  Reject
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Decline Dialog */}
      <Dialog open={declineDialogOpen} onOpenChange={setDeclineDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>
              Decline Purchase Requisition
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Reason for Declining <span className="text-red-500">*</span></Label>
              <Textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="Please provide a reason for declining this purchase requisition..."
                required
                data-testid="decline-reason-input"
              />
              <p className="text-xs text-slate-500">
                The requester will be notified with this reason.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setDeclineDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleDecline}
                disabled={submitting || !declineReason.trim()}
                className="bg-slate-600 hover:bg-slate-700"
                data-testid="confirm-decline-btn"
              >
                {submitting ? <Spinner size={18} className="animate-spin mr-2" /> : <XCircle size={18} className="mr-2" />}
                Decline
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Return to Buyer Dialog */}
      <Dialog open={returnDialogOpen} onOpenChange={setReturnDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>
              Return Requisition to Buyer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <p className="text-sm text-orange-800">
                The buyer will be able to edit and resubmit this requisition after reviewing your feedback.
              </p>
            </div>
            <div className="space-y-2">
              <Label>What needs to be corrected? <span className="text-red-500">*</span></Label>
              <Textarea
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                placeholder="e.g., Incorrect quantity for item #2, missing budget code, wrong item specification..."
                required
                rows={4}
                data-testid="return-reason-input"
              />
              <p className="text-xs text-slate-500">
                This note will be visible to the buyer so they know exactly what to fix.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setReturnDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleReturn}
                disabled={submitting || !returnReason.trim()}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="confirm-return-btn"
              >
                {submitting ? <Spinner size={18} className="animate-spin mr-2" /> : <ArrowRight size={18} className="mr-2" />}
                Return to Buyer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Replenishment Dialog */}
      <Dialog open={rejectReplenishDialog.open} onOpenChange={(o) => setRejectReplenishDialog({ ...rejectReplenishDialog, open: o })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Replenishment — {rejectReplenishDialog.req?.item_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Reason for Rejection <span className="text-red-500">*</span></Label>
              <Textarea value={rejectReplenishReason} onChange={e => setRejectReplenishReason(e.target.value)}
                placeholder="Explain why this replenishment is not needed..." data-testid="reject-replenish-reason" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRejectReplenishDialog({ open: false, req: null })}>Cancel</Button>
              <Button onClick={handleRejectReplenish} disabled={!rejectReplenishReason.trim()}
                className="bg-red-600 hover:bg-red-700" data-testid="confirm-reject-replenish">Reject</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Split by Vendor Dialog */}
      <Dialog open={splitDialog.open} onOpenChange={(o) => setSplitDialog({ ...splitDialog, open: o })}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Split PR by Vendor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <p className="text-sm text-purple-800">Assign each line item to a vendor. Items assigned to the same vendor will be grouped into one PO.</p>
            </div>
            
            <div className="border rounded-lg overflow-hidden" data-testid="split-assignments-table">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700">Item</th>
                    <th className="px-3 py-2 text-center font-semibold text-slate-700 w-16">Qty</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700 w-48">Assign Vendor</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700 w-28">PO Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {splitAssignments.map((a, idx) => (
                    <tr key={idx} className={!a.vendor_id ? 'bg-red-50/30' : ''} data-testid={`split-row-${idx}`}>
                      <td className="px-3 py-2">
                        <p className="font-medium text-slate-800">{a.item_name}</p>
                        <p className="text-xs text-slate-500">{a.unit}</p>
                      </td>
                      <td className="px-3 py-2 text-center">{a.quantity}</td>
                      <td className="px-3 py-2">
                        <select value={a.vendor_id}
                          onChange={e => updateSplitAssignment(idx, 'vendor_id', e.target.value)}
                          className="w-full h-8 rounded-md border border-slate-200 px-2 text-sm"
                          data-testid={`split-vendor-${idx}`}>
                          <option value="">Select vendor...</option>
                          {vendors.map(v => (
                            <option key={v.id} value={v.id}>{v.company_name || v.name}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <select value={a.po_type}
                          onChange={e => updateSplitAssignment(idx, 'po_type', e.target.value)}
                          className="w-full h-8 rounded-md border border-slate-200 px-2 text-sm"
                          data-testid={`split-type-${idx}`}>
                          <option value="local">Local</option>
                          <option value="overseas">Overseas</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Vendor groups summary */}
            {(() => {
              const groups = {};
              splitAssignments.forEach(a => {
                if (a.vendor_id) {
                  if (!groups[a.vendor_id]) groups[a.vendor_id] = { items: 0, vendor: vendors.find(v => v.id === a.vendor_id) };
                  groups[a.vendor_id].items++;
                }
              });
              const groupList = Object.values(groups);
              if (groupList.length === 0) return null;
              return (
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-slate-500 uppercase mb-2">POs to be generated: {groupList.length}</p>
                  {groupList.map((g, i) => (
                    <div key={i} className="flex items-center justify-between text-sm py-1">
                      <span className="font-medium">{g.vendor?.company_name || g.vendor?.name}</span>
                      <span className="text-slate-500">{g.items} item(s)</span>
                    </div>
                  ))}
                </div>
              );
            })()}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setSplitDialog({ open: false, req: null })}>Cancel</Button>
              <Button onClick={handleSplitGenerate} disabled={splitSubmitting}
                className="bg-[#3D1D72] hover:bg-[#2D1652]" data-testid="generate-split-pos-btn">
                {splitSubmitting ? <Spinner size={18} className="animate-spin mr-2" /> : <ArrowRight size={18} className="mr-2" />}
                Generate POs
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Row component for list view
const RequisitionRow = ({ req, isPurchasingManager, onConvert, onDecline, onReturn, onSplit, isProcessing }) => {
  const totalEstimate = req.items.reduce((sum, item) => sum + (item.quantity * item.estimated_unit_price), 0);
  const mappedStatus = STATUS_LABELS[req.status] || req.status;
  const isPending = ['pending', 'pending_conversion', 'queried'].includes(req.status);
  const isConverted = ['approved', 'converted_to_po'].includes(req.status);
  const isDeclined = ['rejected', 'declined'].includes(req.status);
  const isReturned = req.status === 'returned';
  
  return (
    <div className="p-4 hover:bg-slate-50/50 transition-colors rounded-lg transition-shadow" data-testid={`req-row-${req.id}`} data-req-anchor={req.id}>
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[req.status]}`}>
              {mappedStatus}
            </span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${URGENCY_COLORS[req.urgency_level]}`}>
              {req.urgency_level.charAt(0).toUpperCase() + req.urgency_level.slice(1)} Priority
            </span>
            {req.was_previously_returned && isPending && (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700" data-testid={`resubmitted-badge-${req.id}`}>
                Resubmitted
              </span>
            )}
          </div>
          <p className="font-medium text-slate-800">{req.department_name}</p>
          <p className="text-sm text-slate-500">
            {req.items.length} item(s) • Budget: {req.budget_code} • By: {req.requester_name}
          </p>
          <p className="text-sm text-slate-400 mt-1">
            {new Date(req.created_at).toLocaleDateString('en-US', { 
              year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
            })}
          </p>
          {isDeclined && req.decline_reason && (
            <p className="text-sm text-[#E63946] mt-2 bg-red-50 p-2 rounded">
              <strong>Decline Reason:</strong> {req.decline_reason}
            </p>
          )}
          {isReturned && req.return_reason && (
            <p className="text-sm text-orange-700 mt-2 bg-orange-50 p-2 rounded" data-testid={`return-reason-${req.id}`}>
              <strong>Returned:</strong> {req.return_reason}
            </p>
          )}
          {isConverted && req.linked_po_number && (
            <p className="text-sm text-[#2D6A4F] mt-2 bg-green-50 p-2 rounded flex items-center gap-2">
              <CheckCircle size={16} weight="fill" />
              Converted to PO: <span className="font-medium">{req.linked_po_number}</span>
            </p>
          )}
          {req.was_previously_returned && isPending && req.return_history?.length > 0 && (
            <div className="text-sm text-blue-600 mt-2 bg-blue-50 p-2 rounded" data-testid={`return-history-${req.id}`}>
              <strong>Previously returned {req.return_history.length} time(s).</strong> Last reason: {req.return_history[req.return_history.length - 1]?.reason}
            </div>
          )}
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm text-slate-500">Estimated Value</p>
            <p className="text-lg font-bold text-slate-800">${totalEstimate.toFixed(2)}</p>
          </div>
          {isPurchasingManager && isPending && (
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={onConvert}
                className="bg-[#0F52BA] hover:bg-[#0A3D8F]"
                data-testid={`convert-btn-${req.id}`}
                disabled={isProcessing}
              >
                {isProcessing ? <Spinner size={18} className="animate-spin" /> : <ArrowRight size={18} className="mr-1" />}
                Convert to PO
              </Button>
              {req.items?.length > 1 && (
                <Button
                  size="sm"
                  onClick={onSplit}
                  variant="outline"
                  className="text-purple-600 border-purple-300 hover:bg-purple-50"
                  data-testid={`split-btn-${req.id}`}
                  disabled={isProcessing}
                >
                  Split by Vendor
                </Button>
              )}
              <Button
                size="sm"
                onClick={onReturn}
                variant="outline"
                className="text-orange-600 border-orange-300 hover:bg-orange-50"
                data-testid={`return-btn-${req.id}`}
                disabled={isProcessing}
              >
                Return
              </Button>
              <Button
                size="sm"
                onClick={onDecline}
                variant="outline"
                className="text-slate-600 border-slate-300 hover:bg-slate-100"
                data-testid={`decline-btn-${req.id}`}
                disabled={isProcessing}
              >
                <XCircle size={18} className="mr-1" />
                Decline
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Card component for pending conversion view
const PendingConversionCard = ({ req, onConvert, onDecline, onReturn, onSplit, isProcessing }) => {
  const totalEstimate = req.items.reduce((sum, item) => sum + (item.quantity * item.estimated_unit_price), 0);
  const daysSinceSubmitted = Math.floor((new Date() - new Date(req.created_at)) / (1000 * 60 * 60 * 24));
  
  return (
    <div 
      className={`bg-white border-2 rounded-xl p-5 transition-all ${
        req.urgency_level === 'critical' ? 'border-[#E63946] bg-[#E63946]/5' :
        req.urgency_level === 'high' ? 'border-[#F4A261]' :
        'border-slate-200'
      }`}
      data-testid={`pending-card-${req.id}`}
      data-req-anchor={req.id}
    >
      {/* Header with urgency badge */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex flex-wrap gap-2">
          <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${URGENCY_COLORS[req.urgency_level]}`}>
            {req.urgency_level === 'critical' && <Warning size={14} weight="fill" className="mr-1" />}
            {req.urgency_level.charAt(0).toUpperCase() + req.urgency_level.slice(1)} Priority
          </span>
          {req.was_previously_returned && (
            <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700" data-testid={`card-resubmitted-${req.id}`}>
              Resubmitted
            </span>
          )}
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-slate-800">${totalEstimate.toFixed(2)}</p>
          <p className="text-xs text-slate-500">Estimated Value</p>
        </div>
      </div>

      {/* Resubmission notice */}
      {req.was_previously_returned && req.return_history?.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4" data-testid={`card-return-history-${req.id}`}>
          <p className="text-xs font-semibold text-blue-700 mb-1">Previously Returned ({req.return_history.length}x)</p>
          <p className="text-sm text-blue-600">
            Last reason: {req.return_history[req.return_history.length - 1]?.reason}
          </p>
        </div>
      )}

      {/* Department and requester info */}
      <div className="mb-4">
        <h3 className="font-semibold text-lg text-slate-800 mb-1">{req.department_name}</h3>
        <p className="text-sm text-slate-500 mb-2">Budget Code: <span className="font-medium text-slate-700">{req.budget_code}</span></p>
        
        <div className="flex items-center gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-1.5">
            <User size={16} />
            <span>{req.requester_name}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <CalendarBlank size={16} />
            <span>{new Date(req.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
            {daysSinceSubmitted > 0 && (
              <span className="text-[#F4A261] font-medium">({daysSinceSubmitted}d ago)</span>
            )}
          </div>
        </div>
      </div>

      {/* Items list */}
      <div className="bg-slate-50 rounded-lg p-3 mb-4">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-600 mb-2">
          <Package size={16} />
          <span>Requested Items ({req.items.length})</span>
        </div>
        <div className="space-y-1.5">
          {req.items.slice(0, 4).map((item, idx) => (
            <div key={idx} className="flex items-center justify-between text-sm" data-testid={`pending-item-${req.id}-${idx}`}>
              <span className="text-slate-700">{item.item_name}</span>
              <span className="text-slate-500">
                {item.quantity} {item.unit} @ ${item.estimated_unit_price.toFixed(2)}
              </span>
            </div>
          ))}
          {req.items.length > 4 && (
            <p className="text-xs text-slate-400 italic">+{req.items.length - 4} more items</p>
          )}
        </div>
      </div>

      {/* Notes if any */}
      {req.notes && (
        <div className="bg-[#0F52BA]/5 rounded-lg p-3 mb-4">
          <p className="text-xs text-[#0F52BA] font-medium mb-1">Notes</p>
          <p className="text-sm text-slate-600">{req.notes}</p>
        </div>
      )}

      {/* Action buttons - Convert to PO, Split, Return, and Decline */}
      <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
        <Button
          onClick={onConvert}
          className="flex-1 bg-[#0F52BA] hover:bg-[#0A3D8F]"
          disabled={isProcessing}
          data-testid={`pending-convert-${req.id}`}
        >
          {isProcessing ? (
            <Spinner size={18} className="animate-spin mr-2" />
          ) : (
            <ArrowRight size={18} className="mr-2" />
          )}
          Convert to PO
        </Button>
        {req.items?.length > 1 && (
          <Button
            onClick={onSplit}
            variant="outline"
            className="text-purple-600 border-purple-300 hover:bg-purple-50"
            disabled={isProcessing}
            data-testid={`pending-split-${req.id}`}
          >
            Split
          </Button>
        )}
        <Button
          onClick={onReturn}
          variant="outline"
          className="text-orange-600 border-orange-300 hover:bg-orange-50"
          disabled={isProcessing}
          data-testid={`pending-return-${req.id}`}
        >
          Return
        </Button>
        <Button
          onClick={onDecline}
          variant="outline"
          className="text-slate-600 border-slate-300 hover:bg-slate-100"
          disabled={isProcessing}
          data-testid={`pending-decline-${req.id}`}
        >
          <XCircle size={18} className="mr-1" />
          Decline
        </Button>
      </div>
    </div>
  );
};

export default RequisitionsPage;
